"use client";

import clsx from "clsx";

import useSelection from "@/lib/hooks/useSelection";
import textconfig from "@/textconfig";

import { rpc } from "#rpc";
import Chevron from "@/assets/svg/Chevron";
import Skeleton from "@/components/atoms/Skeleton";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import styles from "../order-history.module.css";

export const formatDateToNorwegianLocale = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("nb-NO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const NoOrders = () => <p>{textconfig.account.orderHistory.noOrdersMessage}</p>;

const ClientUI = () => {
  const translations = textconfig.account.recipes;
  const customerToken = useSelection().data?.token;

  const {
    data: orders,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["getOrders", customerToken],
    queryFn: async () => {
      if (customerToken) return await rpc.users.orders(customerToken);
    },
    enabled: !!customerToken,
  });

  const Ribbon = () =>
    !orders || orders?.length === 0 ? null : (
      <div className={clsx(styles.grid, styles.grid__header)}>
        <h2 className={styles.tab__heading}>Ordrenummer</h2>
        <h2 className={styles.tab__heading}>Ordredato</h2>
        <h2 className={styles.tab__heading}>Total</h2>
        <h2 className={styles.tab__heading}>Status</h2>
      </div>
    );

  console.log(orders);

  const Orders = () =>
    isLoading ? (
      <>
        <Skeleton height={"50px"} /> <Skeleton height={"50px"} />
        <Skeleton height={"50px"} />
      </>
    ) : !orders || orders?.length === 0 ? (
      <NoOrders />
    ) : (
      orders?.map((order) => {
        const tosId = (order as any)?.attributes?.shipwallet_tos_id?.data;
        const Summary = () => (
          <>
            <p className={clsx(styles.tab__content, styles.order__number)}>
              <span className={styles.mobile__order__label}>Ordrenummer</span>
              <span className={styles.order__number__span}>
                <Chevron direction="down" />
                {order.order}
              </span>
            </p>

            <p className={styles.tab__content}>
              <span className={styles.mobile__order__label}>
                Bestillingsdato
              </span>
              {order.date && formatDateToNorwegianLocale(order.date)}
            </p>
            <p className={styles.tab__content}>
              <span className={styles.mobile__order__label}>Total</span>
              {`${order.totals?.grandTotalPriceAsNumber} ${order.currency}`}
            </p>
            <p className={styles.tab__content}>
              <span className={styles.mobile__order__label}>Status</span>
              <span className={styles.status}>
                <span>
                  {
                    translations.orderStatus[
                      order?.statusDescription?.toLocaleLowerCase() as keyof typeof translations.orderStatus
                    ]
                  }
                </span>
                {order?.statusDescription?.toLocaleLowerCase() ===
                  "processing" &&
                  tosId && (
                    <div className={styles.trackingNumber}>
                      <Link
                        href={`/sporing?identifier=${tosId}&contact=${order?.shippingAddress?.email}`}
                      >
                        Spor orderen din her
                      </Link>
                    </div>
                  )}
              </span>
            </p>
          </>
        );
        const OrderItem = () =>
          !order.items
            ? null
            : order.items.map((item) => {
                const product = item.product;

                if (!product) return null;

                return (
                  <div
                    className={clsx(
                      styles.expanded__item,
                      styles.grid,
                      styles.expanded__product
                    )}
                    key={item.sku}
                  >
                    <div className={styles.product}>
                      <Image
                        src={(product?.media as any)?.["700x0"]?.[0] ?? ""}
                        width={60}
                        height={75}
                        alt={"Image of product: " + product.name}
                        className={styles.product__image}
                      />
                      <div className={styles.product__details}>
                        <p className={styles.product__name}>{product.name}</p>
                        <p className={styles.product__variant}>
                          {product.variantName}
                        </p>
                        <p className={styles.product__quantity}>
                          {item.quantity} stk
                        </p>
                      </div>
                    </div>
                    <p className={styles.product__price}>
                      {`${item.totalPriceAsNumber} ${order.currency}`}
                    </p>
                  </div>
                );
              });
        const Total = () =>
          !order.totals?.itemsTotalPrice ? null : (
            <div className={clsx(styles.expanded__item, styles.grid)}>
              {order.items?.length && (
                <p className={styles.summary__header}>
                  Delsum
                  <span style={{ opacity: 0.6 }}>{` (${order.items?.length} ${
                    order.items?.length > 1 ? "produkter" : "produkt"
                  })`}</span>
                </p>
              )}
              <p className={styles.product__price}>
                {`${order.totals?.itemsTotalPriceAsNumber} ${order.currency}`}
              </p>
            </div>
          );
        const ShippingTotal = () =>
          !order.totals?.shippingPrice ? null : (
            <div className={clsx(styles.expanded__item, styles.grid)}>
              <p className={styles.summary__header}>Frakt</p>
              <p className={styles.product__price}>
                {`${order.totals?.shippingPriceAsNumber} ${order.currency}`}
              </p>
            </div>
          );
        const Vouchers = () =>
          order.discounts?.vouchers?.map((discount) => {
            if (!!discount)
              return (
                <div
                  className={clsx(styles.expanded__item, styles.grid)}
                  key={discount?.voucher}
                >
                  <p className={styles.summary__header}>{discount.voucher}</p>
                  <p className={styles.product__price}>{discount?.priceOff}</p>
                </div>
              );
          });
        const AutomaticDiscounts = () =>
          order.discounts?.automaticDiscounts?.map((discount) => {
            if (!!discount)
              return (
                <div
                  className={clsx(styles.expanded__item, styles.grid)}
                  key={discount?.name}
                >
                  <p className={styles.summary__header}>{discount?.name}</p>
                  <p className={styles.product__price}>{discount?.priceOff}</p>
                </div>
              );
          });
        const GrandTotal = () => (
          <div className={clsx(styles.expanded__item, styles.grid)}>
            <p className={styles.summary__header}>Total</p>
            <p className={styles.product__price}>
              {`${order.totals?.grandTotalPriceAsNumber} ${order.currency}`}
            </p>
          </div>
        );
        return (
          <details className={styles.order} key={order.order}>
            <summary className={clsx(styles.grid, styles.order__description)}>
              <Summary />
            </summary>
            <div className={styles.expanded}>
              <OrderItem />
              <div className={styles.expanded__summary}>
                <Total />
                <ShippingTotal />
                <Vouchers />
                <AutomaticDiscounts />
                <GrandTotal />
              </div>
            </div>
          </details>
        );
      })
    );

  return (
    <>
      <Ribbon />
      <Orders />
    </>
  );
};

export default ClientUI;
